import { useApolloClient } from '@apollo/client';
import { AlertTriangleIcon, InfoCircleIcon, RefreshIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import { useBankConnectionSync } from '../../hooks/useBankConnectionSync';
import { evictAndRefetchAllQueries } from '../../providers/apolloCache';

interface IBaseSyncingNoticeProps {
  /**
   * Whether connections are actively syncing (no lastSyncedAt yet).
   */
  hasUnsyncedConnections: boolean;
  /**
   * Whether historical sync is still in progress.
   */
  hasIncompleteHistoricalSync: boolean;
  /**
   * Whether there is a potential sync issue.
   */
  hasPotentialSyncIssue: boolean;
}

/**
 * Base visual component for displaying a syncing notice.
 * Use this component directly when you need to control the syncing state manually.
 * Wears the record-page alert pill treatment (`RecordAlertBannerBase`) so the
 * app-wide notices and the in-record banners read as one family.
 */
export const BaseSyncingNotice = ({
  hasUnsyncedConnections,
  hasIncompleteHistoricalSync,
  hasPotentialSyncIssue,
}: IBaseSyncingNoticeProps) => {
  if (!hasUnsyncedConnections && !hasIncompleteHistoricalSync && !hasPotentialSyncIssue) {
    return null;
  }

  let toneClasses: { surface: string; glyph: string; text: string };
  let Icon: React.ComponentType<{ className?: string }>;
  let message: string;

  if (hasPotentialSyncIssue) {
    toneClasses = {
      surface: 'bg-utility-error-50 border-utility-error-100',
      glyph: 'text-utility-error-500',
      text: 'text-utility-error-700',
    };
    Icon = AlertTriangleIcon;
    message =
      "Plaid is having difficulty sending your financial data. We're working on it and will update you by email with a solution.";
  } else if (hasUnsyncedConnections) {
    toneClasses = {
      surface: 'bg-utility-orange-50 border-utility-orange-100',
      glyph: 'text-utility-orange-500',
      text: 'text-utility-orange-700',
    };
    Icon = ({ className }) => <RefreshIcon className={`${className} animate-spin`} />;
    message = 'Plaid is sending your financial information.';
  } else {
    toneClasses = {
      surface: 'bg-utility-warning-50 border-utility-warning-100',
      glyph: 'text-utility-warning-500',
      text: 'text-utility-warning-700',
    };
    Icon = InfoCircleIcon;
    message =
      'Plaid is sending your historical transactions. This may take up to 24 hours. During that time, your product experience will be incomplete.';
  }

  return (
    <div
      className={`${toneClasses.surface} shadow-xs flex min-h-12 w-full items-center gap-2 rounded-2xl border px-4 py-2.5`}
    >
      <Icon className={`${toneClasses.glyph} size-[18px] shrink-0`} />
      <span className={`${toneClasses.text} text-base font-medium`}>{message}</span>
    </div>
  );
};

/**
 * Connected component that uses the useBankConnectionSync hook to determine syncing state.
 * When a sync finishes, it refetches every mounted query in place so the newly
 * synced data shows up across the app.
 */
const SyncingNotice = () => {
  const { hasUnsyncedConnections, hasIncompleteHistoricalSync, hasPotentialSyncIssue } = useBankConnectionSync();
  const client = useApolloClient();
  const prevUnsyncedRef = React.useRef(hasUnsyncedConnections);
  const prevHistoricalRef = React.useRef(hasIncompleteHistoricalSync);

  // Automatically refresh store when either flag transitions from true to false
  React.useEffect(() => {
    const unsyncedStarted = prevUnsyncedRef.current && !hasUnsyncedConnections;
    const historicalStarted = prevHistoricalRef.current && !hasIncompleteHistoricalSync;

    if (unsyncedStarted || historicalStarted) {
      evictAndRefetchAllQueries(client, '[syncing-notice] failed to refetch queries after a sync finished');
    }

    prevUnsyncedRef.current = hasUnsyncedConnections;
    prevHistoricalRef.current = hasIncompleteHistoricalSync;
  }, [hasUnsyncedConnections, hasIncompleteHistoricalSync, client]);

  return (
    <BaseSyncingNotice
      hasUnsyncedConnections={hasUnsyncedConnections}
      hasIncompleteHistoricalSync={hasIncompleteHistoricalSync}
      hasPotentialSyncIssue={hasPotentialSyncIssue}
    />
  );
};

export default SyncingNotice;
